import React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme, keyframes } from '@mui/material/styles';
import {
  CardHeader,
  TextField,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Pencil, Sparkles } from 'lucide-mui';
import { updateTitle } from './selectedVideoSlice';
import { handleEnterKeyPress } from '../../lib/commonUtils';
import { RootState } from '../../reducers';
import { FilePreviewRouteParams } from './types';

const magicalAppear = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.95);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.02);
  }
  100% {
    opacity: 0.7;
    transform: scale(1);
  }
`;

const sparkle = keyframes`
  0% {
    transform: scale(1);
    filter: brightness(1);
  }
  25% {
    transform: scale(1.2);
    filter: brightness(1.3);
  }
  50% {
    transform: scale(1.1);
    filter: brightness(1.2);
  }
  75% {
    transform: scale(1.15);
    filter: brightness(1.25);
  }
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
`;

const VideoTitleCardHeader: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { fileId } = useParams<FilePreviewRouteParams>();
  const { text } = useSelector((state: RootState) => state.selectedVideo.title);
  const postProcess = useSelector(
    (state: RootState) => state.selectedVideo.postProcess
  );

  const [isEditing, setIsEditing] = React.useState(false);
  const [title, setTitle] = React.useState(text);
  const [showSuggestion, setShowSuggestion] = React.useState(false);

  const suggestedTitle = React.useMemo(() => {
    try {
      const summaryResponse =
        postProcess?.systemPromptResponses?.SUMMARY_AND_PROMPTS?.responseText;
      if (summaryResponse) {
        const parsed = JSON.parse(summaryResponse);
        // Take only first line if multiple lines
        return parsed.title?.split('\n')[0] || null;
      }
      return null;
    } catch (e) {
      return null;
    }
  }, [postProcess]);

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
  };

  const updateVideoTitle = () => {
    setIsEditing(false);
    if (fileId && title !== text) {
      dispatch(updateTitle({ fileId, title }));
    }
  };

  const handleSuggestedTitleClick = () => {
    if (suggestedTitle && fileId) {
      const newTitle = suggestedTitle;
      setTitle(newTitle);
      dispatch(updateTitle({ fileId, title: newTitle }));
    }
  };

  React.useEffect(() => {
    if (text) {
      setTitle(text);
    }
  }, [text]);

  return (
    <CardHeader
      sx={{ px: 0, mx: 0 }}
      title={
        isEditing ? (
          <TextField
            value={title}
            onChange={handleTitleChange}
            onBlur={updateVideoTitle}
            onKeyDown={handleEnterKeyPress(updateVideoTitle)}
            autoFocus
            variant="filled"
            multiline
            InputProps={{
              sx: {
                fontSize: '1.25rem',
                fontWeight: 500,
                padding: '1px 0',
                margin: 0,
                borderRadius: '4px !important',
                backgroundColor: `${theme.palette.background.paper} !important`,
                color: theme.palette.text.primary,
                '&:before, &:after': {
                  display: 'none',
                },
                '& .MuiFilledInput-root': {
                  borderRadius: '4px',
                  padding: '4px 6px',
                },
                '& .MuiFilledInput-input': {
                  padding: '1px 0',
                  lineHeight: '1.5',
                },
              },
            }}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                paddingTop: '1px',
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'text',
              padding: '1px 0',
              '&:hover .edit-button': {
                visibility: 'visible',
              },
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                flexGrow: 1,
                fontSize: '1.25rem',
                fontWeight: 500,
                lineHeight: '1.5',
                whiteSpace: 'pre-line',
                margin: 0,
                padding: '1px 0',
                opacity:
                  showSuggestion && suggestedTitle && suggestedTitle !== title
                    ? 0
                    : 1,
                transition: 'opacity 0.2s ease-in-out',
                color: theme.palette.text.primary,
              }}
              data-testid="video-title-Single-video-view"
              onClick={() => setIsEditing(true)}
            >
              {title}
            </Typography>
            {suggestedTitle && suggestedTitle !== title && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: '80px',
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  opacity: showSuggestion ? 0.7 : 0,
                  transition: 'opacity 0.2s ease-in-out',
                  animation: showSuggestion
                    ? `${magicalAppear} 0.5s ease-out`
                    : 'none',
                }}
              >
                <Sparkles
                  sx={{
                    mr: 1,
                    color: theme.palette.text.secondary,
                    flexShrink: 0,
                    animation: showSuggestion
                      ? `${sparkle} 1s ease-out`
                      : 'none',
                  }}
                  fontSize="small"
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    lineHeight: '1.5',
                    color: theme.palette.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {suggestedTitle}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <Tooltip title="Edit title" arrow>
                <IconButton
                  className="edit-button"
                  sx={{
                    visibility: 'hidden',
                    mr: 1,
                    color: theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  data-testid="video-title-edit-icon-button"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil sx={{ width: 16, height: 16 }} />
                </IconButton>
              </Tooltip>
              {suggestedTitle && suggestedTitle !== title && (
                <Tooltip title="Use suggested title" arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSuggestedTitleClick();
                    }}
                    onMouseEnter={() => setShowSuggestion(true)}
                    onMouseLeave={() => setShowSuggestion(false)}
                    size="small"
                    sx={{
                      p: 1,
                      color: theme.palette.text.primary,
                      opacity: 0.5,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        opacity: 1,
                      },
                    }}
                  >
                    <Sparkles fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )
      }
      subheader=""
    />
  );
};

export default VideoTitleCardHeader;
